/**
 * Global request deduplication service to prevent duplicate API calls
 */

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
  abortController: AbortController;
}

class RequestDeduplicationService {
  private pendingRequests = new Map<string, PendingRequest>();
  private cache = new Map<string, { data: any; timestamp: number }>();
  /** Keys detached by clearByUrl whose in-flight responses must not be re-cached */
  private noCacheKeys = new Set<string>();
  private readonly CACHE_DURATION = 90000; // 90 seconds - optimized for lead data
  private readonly REQUEST_TIMEOUT = 15000; // 15 seconds

  /**
   * Get a unique key for the request
   */
  private getRequestKey(url: string, options?: RequestInit): string {
    const method = options?.method || 'GET';
    const body = options?.body ? JSON.stringify(options.body) : '';
    // Exclude context/analytics headers from cache key to improve deduplication
    const headers = options?.headers ? this.getEssentialHeaders(options.headers) : '';
    return `${method}:${url}:${body}:${headers}`;
  }

  /**
   * Extract only essential headers for cache key (exclude analytics/debug headers)
   */
  private getEssentialHeaders(headers: HeadersInit): string {
    const skipHeaders = new Set([
      'x-request-category', 'x-request-page', 'x-request-key',
      'x-request-id', 'x-trace-id',
    ]);
    const obj: Record<string, string> = {};
    if (headers instanceof Headers) {
      headers.forEach((v, k) => { if (!skipHeaders.has(k.toLowerCase())) obj[k] = v; });
    } else if (Array.isArray(headers)) {
      headers.forEach(([k, v]) => { if (!skipHeaders.has(k.toLowerCase())) obj[k] = v; });
    } else {
      Object.entries(headers).forEach(([k, v]) => { if (!skipHeaders.has(k.toLowerCase())) obj[k] = v as string; });
    }
    return JSON.stringify(obj);
  }

  /**
   * Check if cached data is still valid
   */
  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  /**
   * Clean up expired cache entries and pending requests
   */
  private cleanup(): void {
    const now = Date.now();
    
    // Clean expired cache
    for (const [key, { timestamp }] of this.cache.entries()) {
      if (!this.isCacheValid(timestamp)) {
        this.cache.delete(key);
      }
    }
    
    // Clean expired pending requests
    for (const [key, { timestamp, abortController }] of this.pendingRequests.entries()) {
      if (now - timestamp > this.REQUEST_TIMEOUT) {
        abortController.abort();
        this.pendingRequests.delete(key);
      }
    }
  }

  /**
   * Execute a deduplicated request
   */
  async executeRequest<T = any>(
    url: string, 
    options: RequestInit = {},
    useFetch: (url: string, options: RequestInit) => Promise<Response>,
    timeoutMs?: number
  ): Promise<T> {
    const requestKey = this.getRequestKey(url, options);
    
    // Cleanup expired entries
    this.cleanup();
    
    // Check cache first (only for GET requests)
    if ((!options.method || options.method === 'GET') && this.cache.has(requestKey)) {
      const cached = this.cache.get(requestKey)!;
      if (this.isCacheValid(cached.timestamp)) {
        console.log(`🟢 RequestDeduplication: Cache hit for ${url}`);
        return cached.data;
      }
    }
    
    // Check if request is already pending
    if (this.pendingRequests.has(requestKey)) {
      console.log(`🟡 RequestDeduplication: Deduplicating request for ${url}`);
      return this.pendingRequests.get(requestKey)!.promise;
    }
    
    // Create new request
    console.log(`🔵 RequestDeduplication: New request for ${url}`);
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      console.warn(`⏳ Request timeout, aborting: ${url}`);
      abortController.abort();
    }, timeoutMs ?? this.REQUEST_TIMEOUT);

    
    const requestPromise = useFetch(url, {
      ...options,
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          let parsed: any = null;
          try { parsed = text ? JSON.parse(text) : null; } catch {}
          const err: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
          err.status = response.status;
          err.statusText = response.statusText;
          err.detail = parsed?.detail || parsed?.message || text || response.statusText;
          err.body = parsed || text;
          throw err;
        }
        const data = await response.json();
        
        // Cache successful GET requests (skip responses detached by
        // clearByUrl — they started before a mutation and are stale)
        if ((!options.method || options.method === 'GET') && !this.noCacheKeys.has(requestKey)) {
          this.cache.set(requestKey, { data, timestamp: Date.now() });
        }
        
        return data;
      })
      .catch((err) => {
        // Normalize abort error as timeout
        if (err?.name === 'AbortError') {
          const timeoutErr: any = new Error('Request timeout');
          timeoutErr.status = 408;
          timeoutErr.detail = 'The request took too long and was aborted.';
          throw timeoutErr;
        }
        throw err;
      })
      .finally(() => {
        // Cleanup
        clearTimeout(timeoutId);
        this.pendingRequests.delete(requestKey);
        this.noCacheKeys.delete(requestKey);
      });
    
    // Store pending request
    this.pendingRequests.set(requestKey, {
      promise: requestPromise,
      timestamp: Date.now(),
      abortController,
    });
    
    return requestPromise;
  }

  /**
   * Clear all cache and abort pending requests
   */
  clear(): void {
    // Abort all pending requests
    for (const { abortController } of this.pendingRequests.values()) {
      abortController.abort();
    }
    
    this.pendingRequests.clear();
    this.cache.clear();
  }

  /**
   * Clear cache entries whose key contains the given URL substring.
   * Used to force a fresh fetch after a mutation invalidates server state.
   */
  clearByUrl(urlSubstring: string): void {
    for (const key of Array.from(this.cache.keys())) {
      if (key.includes(urlSubstring)) this.cache.delete(key);
    }
    // Detach (do NOT abort) matching in-flight requests so post-mutation
    // callers start a fresh request instead of joining one that began
    // before the mutation. The original caller still resolves normally,
    // but its stale response is blocked from re-populating the cache.
    for (const key of Array.from(this.pendingRequests.keys())) {
      if (key.includes(urlSubstring)) {
        this.pendingRequests.delete(key);
        this.noCacheKeys.add(key);
      }
    }
  }

  /**
   * Get current stats for debugging
   */
  getStats() {
    return {
      pendingRequests: this.pendingRequests.size,
      cacheEntries: this.cache.size,
      timestamp: new Date().toISOString(),
    };
  }
}

// Export singleton instance
export const requestDeduplication = new RequestDeduplicationService();